import { type Auth } from 'firebase/auth';

import { useFriendsList } from '../../components/landing/dashboard/useFriendsList';
import { useAuthSnapshot } from '../hooks/useAuthSnapshot';

/**
 * "Is the bottom tab bar on screen?" — ONE definition, two consumers.
 *
 * WHY THIS MODULE EXISTS (THE-705 / WS4, board feedback 2026-10-02)
 * ----------------------------------------------------------------
 * `<SiteHeader>`'s rule is that a surface with a bottom tab bar lets the "You"
 * tab own account on mobile, so the header's actions slot stays hidden there.
 * The promoted `/liked/:recipientId` board mounts that bar — but the bar is
 * CONDITIONAL, and in the one state it renders nothing (signed out, no
 * recipients on THIS device) the rule would leave the surface with no sign-in
 * door at all. That is the #291 + #301 lockout shape, which
 * `src/__tests__/signInAlwaysReachable.test.tsx` exists to forbid.
 *
 * The resolution is a FALLBACK, not a blanket opt-out: the header shows its door
 * on mobile only when the bar is NOT rendering. That makes the header's mobile
 * visibility a function of the bar's, so the two conditions must be the same
 * condition — written once, here. Two hand-copied predicates would drift the
 * first time the bar's gate changes, and the failure mode of that drift is
 * either a duplicate door or no door, neither of which any per-component test
 * would see.
 *
 * THE THIRD STATE: `resolving`
 * ----------------------------
 * `visible` is a boolean, but "the bar is not visible" has two causes —
 * confirmed-absent, and not-yet-known. A fallback keyed on `!visible` alone
 * would flash the header door during auth/recipient hydration and then retract
 * it. So this returns `resolving` as well, and the fallback holds while it is
 * true. The bar itself needs no such distinction: it hides in both cases.
 *
 * `resolving` is BOUNDED, which matters because it gates the only door:
 *  - the auth leg clears on the first observer callback (`useAuthSnapshot`
 *    flips `ready` on either observer). This adds no new exposure — LikedPage's
 *    door was already `null` until `useIsSignedIn` reported ready.
 *  - the recipient leg clears on the first snapshot OR on `useFriendsList`'s own
 *    `FRIENDS_LIST_TIMEOUT_MS` (10s) escape, whichever lands first. This is the
 *    one place the fallback deliberately diverges from the bar: a bar that stays
 *    hidden forever on a hung Firestore listener is a cosmetic loss, whereas a
 *    door that never appears is a lockout. So a timed-out hydration releases the
 *    hold and the header door appears, while the bar stays hidden.
 */

export interface MobileTabBarVisibilityInputs {
  /** `useAuthSnapshot(...).ready` */
  authReady: boolean;
  /** `useAuthSnapshot(...).uid` */
  uid: string | null;
  /** `useAuthSnapshot(...).isPermanent` */
  isPermanent: boolean;
  /** `useFriendsList(uid).hydrated` */
  friendsHydrated: boolean;
  /** `useFriendsList(uid).friends.length` */
  friendsCount: number;
  /** `useFriendsList(uid).timedOut` — the bounded escape for the hold below. */
  friendsTimedOut: boolean;
}

export interface MobileTabBarVisibility {
  /** True when the bar is on screen. EXACTLY what MobileTabBarMount renders on. */
  visible: boolean;
  /** True while `visible` is not yet knowable. Consumers that would otherwise
   *  flash an alternative affordance (the header's sign-in door) wait on this. */
  resolving: boolean;
}

/**
 * The predicate, as a pure function so it can be truth-tabled directly
 * (`__tests__/mobileTabBarVisibility.test.ts`) without mounting a bar.
 *
 * Show as soon as the visitor has at least one recipient OR is permanent. The
 * permanent clause matters because the bar owns account/notifications on mobile
 * (the top-right avatar is hidden <1024): without it a signed-in user with zero
 * recipients could not reach the You tab. Anon users wait for their first
 * recipient so a cold marketing visitor doesn't get product chrome prematurely.
 */
export function mobileTabBarVisibility(
  i: MobileTabBarVisibilityInputs,
): MobileTabBarVisibility {
  const visible =
    i.authReady &&
    i.uid !== null &&
    (i.isPermanent || (i.friendsHydrated && i.friendsCount > 0));

  // Not-yet-known, as opposed to confirmed-absent. A permanent user is decided
  // by auth alone, so recipient hydration cannot change the answer for them.
  const inFlight =
    !i.authReady || (i.uid !== null && !i.isPermanent && !i.friendsHydrated);

  return { visible, resolving: !visible && inFlight && !i.friendsTimedOut };
}

/** Live `mobileTabBarVisibility` for `auth`. Pass `null` to skip subscribing. */
export function useMobileTabBarVisibility(auth: Auth | null): MobileTabBarVisibility {
  // Both observers, via useAuthSnapshot: onAuthStateChanged alone is silent on
  // the in-place anon→permanent link and left this bar stuck hidden after a
  // fresh signup. See useAuthSnapshot.ts.
  const { uid, isPermanent, ready: authReady } = useAuthSnapshot(auth);
  const { friends, hydrated, timedOut } = useFriendsList(uid);

  return mobileTabBarVisibility({
    authReady,
    uid,
    isPermanent,
    friendsHydrated: hydrated,
    friendsCount: friends.length,
    // `?? false`: older test doubles for useFriendsList predate `timedOut`, and
    // an undefined here must read as "no timeout yet", never as "escape now".
    friendsTimedOut: timedOut ?? false,
  });
}
